import React, { useEffect, useMemo, useState } from "react";
import { Container, Button } from "react-bootstrap";
import {
  ArrowLeft,
  Folder,
  FolderOpen,
  ChevronRight,
} from "lucide-react";
import { useNavigate, useLocation } from "react-router-dom";

import {
  utilityServices,
  utilityGroups,
} from "./servicesConfig";

const GOLD = "#ebb51b";
const GOLD_DARK = "#a67a10";
const BG_HEADER = "#33353a";
const TEXT_ON_PAGE = "#f4f4f5";
const TEXT_MUTED = "#9a9ca1";

const OutlineButton = ({ children, onClick, style, ...props }) => (
  <Button
    onClick={onClick}
    className="uw-outline-btn d-flex align-items-center gap-2 fw-semibold shadow-none border-0"
    style={{
      backgroundColor: "transparent",
      color: "#ebb51b",
      border: `1.5px solid ${GOLD}`,
      borderRadius: "0",
      fontSize: "0.82rem",
      padding: "8px 16px",
      whiteSpace: "nowrap",
      ...style,
    }}
    {...props}
  >
    {children}
  </Button>
);

const NavbarUtility = ({ title }) => {
  const navigate = useNavigate();
  const location = useLocation();

  const activeService = utilityServices.find(
    (service) => service.route === location.pathname
  );

  const displayTitle =
    title || activeService?.title || "Utility Web";

  /*
   * Trova automaticamente il macrogruppo
   * della pagina in cui ci troviamo.
   */
  const currentGroup = useMemo(() => {
    if (!activeService) return null;

    return utilityGroups.find((group) =>
      group.services.includes(activeService.title)
    );
  }, [activeService]);

  const [openGroup, setOpenGroup] = useState(null);

  /*
   * Quando cambio pagina richiudo le cartelle.
   * La cartella attuale rimane comunque evidenziata.
   */
  useEffect(() => {
    setOpenGroup(null);
  }, [location.pathname]);

  const toggleGroup = (groupTitle) => {
    setOpenGroup((current) =>
      current === groupTitle ? null : groupTitle
    );
  };

  return (
    <div className="utility-navbar">
      <Container fluid>
        <div className="utility-navbar-inner">

          {/* HOME */}
          <OutlineButton
            onClick={() => navigate("/home")}
            style={{
              flexShrink: 0,
            }}
          >
            <ArrowLeft size={16} />
            Home
          </OutlineButton>

          {/* TITOLO PAGINA */}
          <div className="utility-current-page">
            <span className="utility-eyebrow">
              Utility Web
            </span>

            <h2>{displayTitle}</h2>
          </div>

          {/* MACROGRUPPI */}
          <div className="utility-groups">
            {utilityGroups.map((group) => {
              const isOpen = openGroup === group.title;

              const isCurrentGroup =
                currentGroup?.title === group.title;

              const services = utilityServices.filter((service) =>
                group.services.includes(service.title)
              );

              return (
                <div
                  key={group.title}
                  className={`
                    utility-folder
                    ${isOpen ? "is-open" : ""}
                    ${isCurrentGroup ? "is-current" : ""}
                  `}
                >
                  {/* CARTELLA */}
                  <button
                    type="button"
                    className="utility-folder-trigger"
                    onClick={() => toggleGroup(group.title)}
                  >
                    <span className="utility-folder-icon">
                      {isOpen ? (
                        <FolderOpen size={19} />
                      ) : (
                        <Folder size={19} />
                      )}
                    </span>

                    <span>{group.title}</span>

                    <ChevronRight
                      size={14}
                      className="utility-folder-chevron"
                    />
                  </button>

                  {/* APP CONTENUTE */}
                  <div className="utility-folder-content">
                    <div className="utility-folder-divider" />

                    {services.map((service) => {
                      const ServiceIcon = service.icon;

                      const isActive =
                        location.pathname === service.route;

                      return (
                        <button
                          key={service.route}
                          type="button"
                          className={`
                            utility-app
                            ${isActive ? "is-active" : ""}
                          `}
                          onClick={() => navigate(service.route)}
                        >
                          <ServiceIcon
                            size={17}
                            color={
                              isActive
                                ? GOLD
                                : TEXT_MUTED
                            }
                          />

                          <span>{service.title}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>

        </div>
      </Container>
    </div>
  );
};

export default NavbarUtility;